import qs from 'qs';
import {
  takeLatest, all, put, call, select,
} from 'redux-saga/effects';
import mime from 'mime-types';
import _ from 'lodash';
import PlayerActionType from 'actions/player/playerActionType';
import NetworkService from 'services/networkService';
import {
  changePlayerLoadingState,
  savePlayerNotes,
  savePlayerNoteTypes,
  changePlayerModalVisibility,
  changePlayerModalLoadingState,
  savePlayerMessages,
  savePlayerMessage,
  savePlayerMessagesTotal,
  changeEndedStatus as changePlayerEndedStatus,
  savePlayerData,
  savePlayerActions,
  changePlayerDataUpdatedStatus,
  savePlayerTimeoutSettings,
  savePlayerDepositSettings,
  savePlayerWithdrawalSettings,
  savePlayerBetSettings,
  savePlayerResponsibleGamingLogs,
  saveResponsibleGamingReminderLimits,
  savePlayerControls,
  savePlayerPersonalInfo,
  savePlayerAccountInfo,
  changePlayerAccountInfoUpdatedStatus,
  changePlayerPersonalInfoUpdatedStatus,
  changePlayerActionEndedStatus,
  savePlayerRealityCheckSettings,
  changePlayerLimitsLoading,
  savePlayerAccessLimitSettings,
  savePlayerAccessLimitOverviewSettings,
  savePlayerAccessLimitPendingSettings,
  savePlayerAccessLimitSettingsLog,
  savePlayerLossLimitSettings,
  savePlayerSingleBetSettings,
  saveHasPlayerTransaction,
  changeHasPlayerTransactionLoadingState,
  savePlayerGameTimeLimitSettings,
  savePlayerLegacyData,
  savePlayerOTP,
  changePlayerNotesLoadingState,
  savePlayerNetDepositSettings,
} from 'actions/player/playerActionCreator';
import enumTypes from 'constants/enumTypes';
import {
  setSuccessMessage, setErrorMessage, changeLoadingState, changeEndedStatus,
} from 'app/settings/slices';
import {
  selectPlayerAccessLimitSettings, selectPlayerAccountBalance, selectPlayerData, selectPlayerMessages, selectPlayerMessagesTotal, selectPlayerWithdrawalSettings,
} from 'selectors';
import { i18n } from 'services';
import { getError, getMessage, getResourceValue } from 'utils/helpers';
import { playerBlockedStatuses } from 'constants/mapping';
import { AppConstants } from 'constants/index';
import { downloadFile, isJSON } from 'utils/utils';

function* getPlayerData({ payload }) {
  try {
    yield put(changeLoadingState(true));
    const { PlayersAdmin, Player, GetPlayerData } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, Player, GetPlayerData], { params: payload });
    yield put(savePlayerData(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err, true);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* editPlayer({ payload }) {
  try {
    const { playerId, data } = payload;
    const { firstName, lastName } = data;
    const { PlayersAdmin, Player } = AppConstants.api;
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Player, playerId], { data });
    yield put(changeLoadingState(false));
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(changePlayerAccountInfoUpdatedStatus(true));
    yield put(changePlayerPersonalInfoUpdatedStatus(true));
    yield put(setSuccessMessage(getMessage(i18n.t('lPlayer'), i18n.t('notification:slUpdated'), `${!_.isNil(firstName) ? firstName : '-'} ${!_.isNil(lastName) ? lastName : '-'}`)));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      let error = getError(err);
      const errorCodes = ['PlayerMobileNumberInUse', 'PlayerEmailInUse'];
      const isDuplicatedPlayer = err?.response?.data.some(({ errorCode }) => errorCodes.includes(errorCode));
      if (isDuplicatedPlayer) {
        error = {
          errors: err?.response?.data.map((el) => ({
            ...el,
            errorMessage: i18n.t(`player:${el.errorCode}`),
          })),
        };
      }
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerNotes({ payload }) {
  try {
    yield put(changePlayerNotesLoadingState(true));
    const options = {
      params: payload.data,
      paramsSerializer: (params) => qs.stringify(params),
    };
    const { PlayersAdmin, PlayerNote } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, PlayerNote], options);
    yield put(savePlayerNotes(data));
    yield put(changePlayerNotesLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerNotesLoadingState(false));
  }
}

function* getPlayerNoteTypes({ payload }) {
  const { isLoading } = payload;
  try {
    if (isLoading) {
      yield put(changeLoadingState(true));
    }
    const { PlayersAdmin, PlayerNote, GetNoteTypes } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, PlayerNote, GetNoteTypes]);
    yield put(savePlayerNoteTypes(data));
    if (isLoading) {
      yield put(changeLoadingState(false));
    }
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    if (isLoading) {
      yield put(changeLoadingState(false));
    }
  }
}

function* exportPlayerNotes({ payload }) {
  try {
    const options = {
      params: payload.data,
      responseType: 'blob',
      paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
    };
    yield put(changeLoadingState(true));
    const { ReportingAdmin, Player, Export } = AppConstants.api;
    const { data, headers } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, Export, payload.playerId], options);
    downloadFile(data, i18n.t('player:playerNotes'), mime.extension(headers['content-type']));
    yield put(changePlayerEndedStatus(true));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* addPlayerNote({ payload }) {
  try {
    yield put(changeLoadingState(true));
    const { data, playerId } = payload;
    const { PlayersAdmin, PlayerNote } = AppConstants.api;
    yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, PlayerNote, playerId], { data });
    yield put(changeLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('player:notes'), i18n.t('notification:slAdded'))));
    yield put(changeEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerMassMessages({ payload }) {
  try {
    const options = {
      params: payload.data,
      paramsSerializer: (params) => qs.stringify(params),
    };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, MassMessages, ByPlayer } = AppConstants.api;
    const response = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, ByPlayer, payload.playerId], options);
    const { data, total } = response.data;
    const state = yield select();
    const messagesState = selectPlayerMessages(state.player);
    const totalState = selectPlayerMessagesTotal(state.player);
    yield put(
      savePlayerMessages({
        ...messagesState,
        sent: data,
      }),
    );
    yield put(
      savePlayerMessagesTotal({
        ...totalState,
        sent: total,
      }),
    );
    yield put(changeLoadingState(false));
  } catch (err) {
    yield put(changeLoadingState(false));
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
  }
}

function* getPlayerMessage({ payload }) {
  try {
    const { playerId, messageId } = payload;
    const params = { messageId };
    yield put(changeLoadingState(true));
    const { NotificationAdmin, MassMessages, MessageByPlayer } = AppConstants.api;
    const response = yield call(NetworkService.makeAPIGetRequest, [NotificationAdmin, MassMessages, MessageByPlayer, playerId], { params });
    yield put(savePlayerMessage(response.data));
    yield put(changeLoadingState(false));
  } catch (err) {
    yield put(changeLoadingState(false));
    if (err && !isJSON(err.message)) {
      const error = getError(err, true);
      yield put(setErrorMessage(error));
    }
  }
}

function* getPlayerControls({ payload }) {
  try {
    const { BuilderAdmin, UiCustomization, GetPlayerProfileControls } = AppConstants.api;
    yield put(changePlayerLoadingState(true));
    const response = yield call(NetworkService.makeAPIGetRequest, [BuilderAdmin, UiCustomization, GetPlayerProfileControls], payload);
    yield put(savePlayerControls(response.data));
    yield put(changePlayerLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLoadingState(false));
  }
}

function* editPlayerMarkAttention({ payload }) {
  try {
    const { PlayersAdmin, Actions, Attention } = AppConstants.api;
    const { params, data, playerId } = payload;
    const options = {
      data,
      params,
    };
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, Attention, playerId], options);
    yield put(changeLoadingState(false));
    yield put(changePlayerDataUpdatedStatus(true));
    const attentionLevel = +params.level;
    if (attentionLevel) {
      yield put(setSuccessMessage(i18n.t('notification:setMarkAttention', { name: getResourceValue('markAttentionLevels', attentionLevel, enumTypes.markAttentionLevels).toLowerCase() })));
    } else {
      yield put(setSuccessMessage(getMessage(i18n.t('player:markAttention'), i18n.t('notification:slRemoved'))));
    }
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* unlockPlayer({ payload }) {
  try {
    const { PlayersAdmin, Actions, Unlock } = AppConstants.api;
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, Unlock, payload.playerId]);
    yield put(changeLoadingState(false));
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(changePlayerAccountInfoUpdatedStatus(true));
    yield put(setSuccessMessage(getMessage(i18n.t('account'), i18n.t('notification:slUnlocked'))));
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* changePlayerStatus({ payload }) {
  try {
    const { PlayersAdmin, Actions, Status } = AppConstants.api;
    const { params, data, playerId } = payload;
    const options = {
      data,
      params,
    };
    yield put(changeLoadingState(true));
    const response = yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, Status, playerId], options);
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(changePlayerAccountInfoUpdatedStatus(true));
    yield put(changeLoadingState(false));
    if (params.isSet === false) {
      yield put(setSuccessMessage(getMessage(i18n.t('status'), i18n.t('notification:slChanged'))));
    } else if (+params.status === enumTypes.playerStatuses.Active) {
      if (response.data) {
        yield put(setSuccessMessage(getMessage(i18n.t('account'), i18n.t('notification:slActivated'))));
      } else {
        yield put(setSuccessMessage(getMessage(i18n.t('account'), i18n.t('notification:slReactivated'))));
      }
    } else {
      yield put(setSuccessMessage(i18n.t('notification:changeAccountStatus', { action: getResourceValue('lPlayerBlockedStatuses', params.status, _.invert(playerBlockedStatuses)) })));
    }
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* blockPlayerActivities({ payload }) {
  try {
    const { PlayersAdmin, Actions, BlockActivities } = AppConstants.api;
    const { data, playerId } = payload;
    const options = {
      data,
    };
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, BlockActivities, playerId], options);
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(changePlayerAccountInfoUpdatedStatus(true));
    yield put(changeLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('activities'), i18n.t('notification:plBlocked'))));
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* unblockPlayerActivities({ payload }) {
  try {
    const { PlayersAdmin, Actions, UnblockActivities } = AppConstants.api;
    const { data, playerId } = payload;
    const options = {
      data,
    };
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, UnblockActivities, playerId], options);
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(changePlayerAccountInfoUpdatedStatus(true));
    yield put(changeLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('activities'), i18n.t('notification:plUnblocked'))));
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerActions({ payload }) {
  try {
    const options = {
      params: payload.params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    yield put(changePlayerLoadingState(true));
    const { ReportingAdmin, Player, Actions } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, Actions, payload.id], options);
    yield put(changePlayerLoadingState(false));
    yield put(changePlayerModalVisibility(false));
    yield put(savePlayerActions(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLoadingState(false));
  }
}

function* exportPlayerActions({ payload }) {
  try {
    const { params, id } = payload;
    const options = {
      params,
      paramsSerializer: (param) => qs.stringify(param, { allowDots: true }),
      responseType: 'blob',
    };
    yield put(changeLoadingState(true));
    const {
      ReportingAdmin, Player, Actions, Export,
    } = AppConstants.api;
    const { data, headers } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, Actions, Export, id], options);
    downloadFile(data, i18n.t('player:actionLogs'), mime.extension(headers['content-type']));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* closePlayerAccount({ payload }) {
  try {
    const { data, playerId } = payload;
    const options = {
      data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, Actions } = AppConstants.api;
    yield call(NetworkService.makeAPIDeleteRequest, [PlayersAdmin, Actions, playerId], options);
    yield put(changeLoadingState(false));
    yield put(changeEndedStatus(true));
    const state = yield select();
    let player = selectPlayerData(state.player);
    player = { ...player, isDeleted: true };
    const accountBalance = selectPlayerAccountBalance(state.player);
    const tempData = { player, accountBalance };
    yield put(savePlayerData(tempData));
    yield put(setSuccessMessage(getMessage(i18n.t('account'), i18n.t('notification:slClosed'))));
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* activateDormantPlayer({ payload }) {
  try {
    const { playerId } = payload;
    yield put(changeLoadingState(true));
    const { PlayersAdmin, Actions, Dormant } = AppConstants.api;
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, Dormant, playerId]);
    yield put(changeLoadingState(false));
    const state = yield select();
    let player = selectPlayerData(state.player);
    player = { ...player, isDormant: false };
    const accountBalance = selectPlayerAccountBalance(state.player);
    const tempData = { player, accountBalance };
    yield put(savePlayerData(tempData));
    yield put(changeEndedStatus(true));
    yield put(setSuccessMessage(getMessage(i18n.t('account'), i18n.t('notification:slActivated'))));
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* updatePlayerVerification({ payload }) {
  try {
    const { playerId, params } = payload;
    const { isSet, name, verification } = params;
    const options = {
      params: { verification, isSet },
    };
    yield put(changePlayerModalLoadingState(true));
    const { PlayersAdmin, Player, Verification } = AppConstants.api;
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Player, playerId, Verification], options);
    yield put(changePlayerModalLoadingState(false));
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(changePlayerAccountInfoUpdatedStatus(true));
    yield put(setSuccessMessage(getMessage(name, i18n.t(isSet ? 'notification:slVerified' : 'notification:slUnverified'))));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerModalLoadingState(false));
  }
}

function* updatePlayerContactInfoVerification({ payload }) {
  try {
    const { playerId, params } = payload;
    const { isSet, name, verification } = params;
    yield put(changePlayerModalLoadingState(true));
    const {
      PlayersAdmin, Player, Verification, ContactInfo,
    } = AppConstants.api;
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Player, playerId, Verification, ContactInfo], { params: { verification, isSet } });
    yield put(changePlayerModalLoadingState(false));
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(changePlayerAccountInfoUpdatedStatus(true));
    yield put(setSuccessMessage(getMessage(name, i18n.t(isSet ? 'notification:slVerified' : 'notification:slUnverified'))));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerModalLoadingState(false));
  }
}

function* getPlayerTimeoutSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, Timeout } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Timeout, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, Timeout], options);
    yield put(savePlayerTimeoutSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Timeout, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Timeout, false));
  }
}

function* editPlayerTimeoutSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, Timeout } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, Timeout], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerTimeoutSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.Timeout, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerDepositSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, Deposit } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Deposit, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, Deposit], options);
    yield put(savePlayerDepositSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Deposit, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Deposit, false));
  }
}

function* editPlayerDepositSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, Deposit } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, Deposit], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerDepositSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.Deposit, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerNetDepositSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, NetDeposit } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.NetDeposit, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, NetDeposit], options);
    yield put(savePlayerNetDepositSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.NetDeposit, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.NetDeposit, false));
  }
}

function* editPlayerNetDepositSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, NetDeposit } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, NetDeposit], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerNetDepositSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.NetDeposit, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerWithdrawalSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, Withdrawal } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Withdrawal, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, Withdrawal], options);
    yield put(savePlayerWithdrawalSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Withdrawal, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Withdrawal, false));
  }
}

function* editPlayerWithdrawalSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, Withdrawal } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, Withdrawal], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerWithdrawalSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.Withdrawal, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerBetSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, Bet } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Bet, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, Bet], options);
    yield put(savePlayerBetSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Bet, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.Bet, false));
  }
}

function* editPlayerBetSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, Bet } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, Bet], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerBetSettings(data));
    yield put(changeLoadingState(false));
    const state = yield select();
    const settings = selectPlayerWithdrawalSettings(state.player);
    yield put(
      setSuccessMessage(
        getMessage(
          getResourceValue('limitationTypes', enumTypes.limitationTypes.Bet, enumTypes.limitationTypes),
          i18n.t(Object.keys(settings).length ? 'notification:slUpdated' : 'notification:slSet'),
        ),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerRealityCheckSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, RealityCheck } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.RealityCheck, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, RealityCheck], options);
    yield put(savePlayerRealityCheckSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.RealityCheck, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.RealityCheck, false));
  }
}

function* editPlayerRealityCheckSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, RealityCheck } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, RealityCheck], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerRealityCheckSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.RealityCheck, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerResponsibleGamingLogs({ payload }) {
  try {
    const options = {
      params: payload.params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    yield put(changeLoadingState(true));
    const { ReportingAdmin, ResponsibleGaming, ResponsibleGamingHistory } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, ResponsibleGaming, ResponsibleGamingHistory], options);
    yield put(savePlayerResponsibleGamingLogs(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerResponsibleGamingReminderLimits({ payload }) {
  try {
    const { playerId } = payload;
    yield put(changeLoadingState(true));
    const { ReportingAdmin, ResponsibleGaming, Remainder } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, ResponsibleGaming, Remainder, playerId]);
    yield put(saveResponsibleGamingReminderLimits(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerPersonalInfo({ payload }) {
  const { id, isLoading } = payload;
  try {
    if (isLoading) {
      yield put(changeLoadingState(true));
    }

    const { ReportingAdmin, Player, Personal } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, id, Personal]);

    if (isLoading) {
      yield put(changeLoadingState(false));
    }
    yield put(savePlayerPersonalInfo(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    if (isLoading) {
      yield put(changeLoadingState(false));
    }
  }
}

function* getPlayerAccountInfo({ payload }) {
  const { isLoading, id } = payload;
  try {
    if (isLoading) {
      yield put(changeLoadingState(true));
    }

    const { ReportingAdmin, Player, Account } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, id, Account]);

    if (isLoading) {
      yield put(changeLoadingState(false));
    }

    yield put(savePlayerAccountInfo(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    if (isLoading) {
      yield put(changeLoadingState(false));
    }
  }
}

function* disablePlayerLoginTFA({ payload }) {
  try {
    const { PlayersAdmin, Actions, Reset2FA } = AppConstants.api;
    const { data, playerId } = payload;
    const options = {
      data,
    };
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, Reset2FA, playerId], options);
    yield put(changeLoadingState(false));
    yield put(changePlayerDataUpdatedStatus(true));
    yield put(setSuccessMessage(getMessage(i18n.t('player:tFA'), i18n.t('notification:slDisabled'))));
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerAccessLimitSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, AccessLimit } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.AccessLimit, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, AccessLimit], options);
    yield put(savePlayerAccessLimitSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.AccessLimit, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.AccessLimit, false));
  }
}

function* getPlayerAccessLimitOverviewSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, AccessLimitOverview } = AppConstants.api;

    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, AccessLimitOverview], options);
    yield put(savePlayerAccessLimitOverviewSettings(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerAccessLimitPendingSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, AccessLimitPending } = AppConstants.api;

    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, AccessLimitPending], options);
    yield put(savePlayerAccessLimitPendingSettings(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* editPlayerAccessLimitSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, AccessLimit } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, AccessLimit], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerAccessLimitSettings(data));
    yield put(changeLoadingState(false));
    const state = yield select();
    const settings = selectPlayerAccessLimitSettings(state.player);
    yield put(
      setSuccessMessage(
        getMessage(
          getResourceValue('limitationTypes', enumTypes.limitationTypes.AccessLimit, enumTypes.limitationTypes),
          i18n.t(Object.keys(settings).length ? 'notification:slUpdated' : 'notification:slSet'),
        ),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerAccessLimitSettingsLog({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { ReportingAdmin, ResponsibleGaming, AccessLimitLog } = AppConstants.api;

    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, ResponsibleGaming, AccessLimitLog], options);
    yield put(savePlayerAccessLimitSettingsLog(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerLossLimitSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, Loss } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.LossLimit, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, Loss], options);
    yield put(savePlayerLossLimitSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.LossLimit, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.LossLimit, false));
  }
}

function* editPlayerLossLimitSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, Loss } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, Loss], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerLossLimitSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.LossLimit, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerSingleBetSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, SingleBet } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.SingleBet, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, SingleBet], options);
    yield put(savePlayerSingleBetSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.SingleBet, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.SingleBet, false));
  }
}

function* editPlayerSingleBetSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, SingleBet } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, SingleBet], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerLossLimitSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.SingleBet, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getHasPlayerTransaction({ payload }) {
  try {
    const { playerId } = payload;
    const { ReportingAdmin, Player, HasTransaction } = AppConstants.api;
    yield put(changeHasPlayerTransactionLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, playerId, HasTransaction]);
    yield put(saveHasPlayerTransaction(data));
    yield put(changeHasPlayerTransactionLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeHasPlayerTransactionLoadingState(false));
  }
}

function* getPlayerGameTimeLimitSettings({ payload }) {
  try {
    const options = {
      params: payload.params,
    };
    const { PlayersAdmin, ResponsibleGaming, Time } = AppConstants.api;

    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.GameTimeLimit, true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, ResponsibleGaming, Time], options);
    yield put(savePlayerGameTimeLimitSettings(data));
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.GameTimeLimit, false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changePlayerLimitsLoading(enumTypes.limitationTypes.GameTimeLimit, false));
  }
}

function* editPlayerGameTimeLimitSettings({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeLoadingState(true));
    const { PlayersAdmin, ResponsibleGaming, Time } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIPostRequest, [PlayersAdmin, ResponsibleGaming, Time], options);
    yield put(changeEndedStatus(true));
    yield put(savePlayerGameTimeLimitSettings(data));
    yield put(changeLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(getResourceValue('limitationTypes', enumTypes.limitationTypes.GameTimeLimit, enumTypes.limitationTypes), i18n.t(payload.isEdit ? 'notification:slUpdated' : 'notification:slSet')),
      ),
    );
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerLegacyData({ payload }) {
  try {
    const { ReportingAdmin, Player, LegacyData } = AppConstants.api;
    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [ReportingAdmin, Player, payload.id, LegacyData]);
    yield put(savePlayerLegacyData(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* setPlayerOTP({ payload }) {
  try {
    const { PlayersAdmin, Actions, SetOTP } = AppConstants.api;
    const { data, playerId } = payload;
    const options = {
      data,
    };
    yield put(changeLoadingState(true));
    yield call(NetworkService.makeAPIPutRequest, [PlayersAdmin, Actions, SetOTP, playerId], options);
    yield put(changeLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('player:otp'), i18n.t('notification:slSet'))));
    yield put(changePlayerActionEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

function* getPlayerOTP({ payload }) {
  try {
    const { PlayersAdmin, Player, OTP } = AppConstants.api;
    yield put(changeLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [PlayersAdmin, Player, payload.playerId, OTP]);
    yield put(savePlayerOTP(data));
    yield put(changeLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeLoadingState(false));
  }
}

export default function* playerSaga() {
  yield all([
    takeLatest(PlayerActionType.GET_PLAYER_DATA, getPlayerData),
    takeLatest(PlayerActionType.EDIT_PLAYER, editPlayer),
    takeLatest(PlayerActionType.GET_PLAYER_NOTES, getPlayerNotes),
    takeLatest(PlayerActionType.GET_PLAYER_NOTE_TYPES, getPlayerNoteTypes),
    takeLatest(PlayerActionType.EXPORT_PLAYER_NOTES, exportPlayerNotes),
    takeLatest(PlayerActionType.ADD_PLAYER_NOTE, addPlayerNote),
    takeLatest(PlayerActionType.GET_PLAYER_MASS_MESSAGES, getPlayerMassMessages),
    takeLatest(PlayerActionType.GET_PLAYER_MESSAGE, getPlayerMessage),
    takeLatest(PlayerActionType.GET_PLAYER_CONTROLS, getPlayerControls),
    takeLatest(PlayerActionType.EDIT_PLAYER_MARK_ATTENTION, editPlayerMarkAttention),
    takeLatest(PlayerActionType.UNLOCK_PLAYER, unlockPlayer),
    takeLatest(PlayerActionType.CHANGE_PLAYER_STATUS, changePlayerStatus),
    takeLatest(PlayerActionType.BLOCK_PLAYER_ACTIVITIES, blockPlayerActivities),
    takeLatest(PlayerActionType.UNBLOCK_PLAYER_ACTIVITIES, unblockPlayerActivities),
    takeLatest(PlayerActionType.GET_PLAYER_ACTIONS, getPlayerActions),
    takeLatest(PlayerActionType.CLOSE_PLAYER_ACCOUNT, closePlayerAccount),
    takeLatest(PlayerActionType.ACTIVATE_DORMANT_PLAYER, activateDormantPlayer),
    takeLatest(PlayerActionType.UPDATE_PLAYER_VERIFICATION, updatePlayerVerification),
    takeLatest(PlayerActionType.UPDATE_PLAYER_CONTACT_INFO_VERIFICATION, updatePlayerContactInfoVerification),
    takeLatest(PlayerActionType.GET_PLAYER_TIMOUT_SETTINGS, getPlayerTimeoutSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_TIMOUT_SETTINGS, editPlayerTimeoutSettings),
    takeLatest(PlayerActionType.GET_PLAYER_DEPOSIT_SETTINGS, getPlayerDepositSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_DEPOSIT_SETTINGS, editPlayerDepositSettings),
    takeLatest(PlayerActionType.GET_PLAYER_NET_DEPOSIT_SETTINGS, getPlayerNetDepositSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_NET_DEPOSIT_SETTINGS, editPlayerNetDepositSettings),
    takeLatest(PlayerActionType.GET_PLAYER_WITHDRAWAL_SETTINGS, getPlayerWithdrawalSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_WITHDRAWAL_SETTINGS, editPlayerWithdrawalSettings),
    takeLatest(PlayerActionType.GET_PLAYER_BET_SETTINGS, getPlayerBetSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_BET_SETTINGS, editPlayerBetSettings),
    takeLatest(PlayerActionType.GET_PLAYER_REALITY_CHECK_SETTINGS, getPlayerRealityCheckSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_REALITY_CHECK_SETTINGS, editPlayerRealityCheckSettings),
    takeLatest(PlayerActionType.GET_RESPONSIBLE_GAMING_LOGS, getPlayerResponsibleGamingLogs),
    takeLatest(PlayerActionType.GET_RESPONSIBLE_GAMING_REMINDER_LIMITS, getPlayerResponsibleGamingReminderLimits),
    takeLatest(PlayerActionType.EXPORT_PLAYER_ACTIONS, exportPlayerActions),
    takeLatest(PlayerActionType.GET_PLAYER_ACCOUNT_INFO, getPlayerAccountInfo),
    takeLatest(PlayerActionType.GET_PLAYER_PERSONAL_INFO, getPlayerPersonalInfo),
    takeLatest(PlayerActionType.DISABLE_PLAYER_LOGIN_TFA, disablePlayerLoginTFA),
    takeLatest(PlayerActionType.GET_PLAYER_ACCESS_LIMIT_SETTINGS, getPlayerAccessLimitSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_ACCESS_LIMIT_SETTINGS, editPlayerAccessLimitSettings),
    takeLatest(PlayerActionType.GET_PLAYER_ACCESS_LIMIT_OVERVIEW_SETTINGS, getPlayerAccessLimitOverviewSettings),
    takeLatest(PlayerActionType.GET_PLAYER_ACCESS_LIMIT_PENDING_SETTINGS, getPlayerAccessLimitPendingSettings),
    takeLatest(PlayerActionType.GET_PLAYER_ACCESS_LIMIT_SETTINGS_LOG, getPlayerAccessLimitSettingsLog),
    takeLatest(PlayerActionType.GET_PLAYER_LOSS_LIMIT_SETTINGS, getPlayerLossLimitSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_LOSS_LIMIT_SETTINGS, editPlayerLossLimitSettings),
    takeLatest(PlayerActionType.GET_PLAYER_SINGLE_BET_SETTINGS, getPlayerSingleBetSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_SINGLE_BET_SETTINGS, editPlayerSingleBetSettings),
    takeLatest(PlayerActionType.GET_HAS_PLAYER_TRANSACTION, getHasPlayerTransaction),
    takeLatest(PlayerActionType.GET_PLAYER_GAME_TIME_LIMIT_SETTINGS, getPlayerGameTimeLimitSettings),
    takeLatest(PlayerActionType.EDIT_PLAYER_GAME_TIME_LIMIT_SETTINGS, editPlayerGameTimeLimitSettings),
    takeLatest(PlayerActionType.GET_PLAYER_LEGACY_DATA, getPlayerLegacyData),
    takeLatest(PlayerActionType.GET_PLAYER_OTP, getPlayerOTP),
    takeLatest(PlayerActionType.SET_PLAYER_OTP, setPlayerOTP),
  ]);
}
